/**
 * WordConfetti
 *
 * 텍스트 기반 Confetti 효과
 * - 올해의 스레드에서 추출한 단어들이 화면 중앙에서 펑 터지는 애니메이션
 * - Framer Motion 기반
 * - 페이지 로드 직후 1회만 실행
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@mui/material/styles';

/**
 * 텍스트에서 단어 추출
 */
const extractWords = (yearEndData, count = 25) => {
  if (!yearEndData) return [];

  const allTexts = [];

  // yearOfTheThreads에서 추출
  if (yearEndData.yearOfTheThreads?.threads) {
    yearEndData.yearOfTheThreads.threads.forEach(item => {
      if (item?.thread?.text) allTexts.push(item.thread.text);
    });
  }

  // notableThreads에서 추출
  if (yearEndData.notableThreads) {
    Object.values(yearEndData.notableThreads).forEach(item => {
      if (item?.thread?.text) allTexts.push(item.thread.text);
    });
  }

  // monthlyReport에서 추출
  if (yearEndData.monthlyReport) {
    Object.values(yearEndData.monthlyReport).forEach(month => {
      if (month?.topThreads) {
        month.topThreads.forEach(thread => {
          if (thread?.text) allTexts.push(thread.text);
        });
      }
    });
  }

  // 텍스트 합치기
  const combinedText = allTexts.join(' ');

  // 단어 추출 및 필터링
  const words = combinedText
    .replace(/https?:\/\/\S+/g, '') // URL 제거
    .replace(/@\w+/g, '') // 멘션 제거
    .replace(/#\w+/g, '') // 해시태그 제거
    .replace(/[^\w\sㄱ-ㅎㅏ-ㅣ가-힣]/g, '') // 특수문자 제거
    .split(/\s+/)
    .filter(w => w.length >= 2 && w.length <= 8) // 2-8자
    .filter(w => !/^\d+$/.test(w)); // 숫자만 있는 단어 제거

  // 중복 제거 후 셔플
  const uniqueWords = [...new Set(words)];
  const shuffled = uniqueWords.sort(() => Math.random() - 0.5);

  return shuffled.slice(0, count);
};

/**
 * 개별 단어 파티클 - 폭발 효과
 */
const WordParticle = ({ word, index, totalCount }) => {
  const theme = useTheme();

  // 랜덤 값 (컴포넌트 마운트 시 고정)
  const randomValues = useMemo(() => {
    // 360도 균등 분배 + 랜덤 오프셋
    const angle = (index / totalCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.8;

    // 폭발 거리 (더 넓게)
    const distance = 300 + Math.random() * 500;

    // 회전 (더 과격하게)
    const rotation = Math.random() * 1080 - 540;

    // 스케일 (더 크게)
    const scale = 1 + Math.random() * 1.5;

    // 시간 (더 빠르게 시작)
    const duration = 1.2 + Math.random() * 0.8;

    // 색상 변형
    const colors = [
      theme.palette.primary.main,
      theme.palette.primary.light,
      theme.palette.primary.dark,
      theme.palette.secondary.main,
      theme.palette.text.primary,
    ];
    const color = colors[Math.floor(Math.random() * colors.length)];

    // 폰트 크기 (더 크게)
    const fontSizes = [16, 20, 24, 28, 32, 40];
    const fontSize = fontSizes[Math.floor(Math.random() * fontSizes.length)];

    // 중력 효과 (랜덤)
    const gravity = 100 + Math.random() * 200;

    return { angle, distance, rotation, scale, duration, color, fontSize, gravity };
  }, [index, totalCount, theme]);

  const { angle, distance, rotation, scale, duration, color, fontSize, gravity } = randomValues;

  // 끝점: 방사형으로 폭발
  const endX = Math.cos(angle) * distance;
  const endY = Math.sin(angle) * distance + gravity;

  return (
    <motion.span
      initial={{
        x: 0,
        y: 0,
        scale: 0,
        rotate: 0,
        opacity: 0,
      }}
      animate={{
        x: [0, endX * 0.3, endX],
        y: [0, endY * 0.2 - 50, endY],
        scale: [0, scale * 1.5, scale, scale * 0.8],
        rotate: [0, rotation * 0.5, rotation],
        opacity: [0, 1, 1, 1, 0],
      }}
      transition={{
        duration: duration,
        delay: index * 0.015, // 더 빠른 stagger
        ease: [0.16, 1, 0.3, 1], // 폭발적인 easeOutExpo
        times: [0, 0.15, 1], // 초반에 빠르게
        opacity: {
          times: [0, 0.05, 0.3, 0.7, 1],
          duration: duration,
        },
        scale: {
          times: [0, 0.1, 0.4, 1],
          duration: duration,
        },
      }}
      style={{
        position: 'absolute',
        fontSize: `${fontSize}px`,
        fontWeight: 700,
        color: color,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        willChange: 'transform, opacity',
        fontFamily: '"Pretendard", "Noto Sans KR", sans-serif',
        textShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}
    >
      {word}
    </motion.span>
  );
};

/**
 * WordConfetti 메인 컴포넌트
 */
const WordConfetti = ({ yearEndData, onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);

  // 단어 추출 (더 많이)
  const words = useMemo(() => extractWords(yearEndData, 40), [yearEndData]);

  // 애니메이션 완료 후 자동 제거
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, 2800); // 폭발 후 빠르게 정리

    return () => clearTimeout(timer);
  }, [onComplete]);

  if (!words.length) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        >
          {/* 중앙 기준점 */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {words.map((word, index) => (
              <WordParticle
                key={`${word}-${index}`}
                word={word}
                index={index}
                totalCount={words.length}
              />
            ))}
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WordConfetti;
